import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Form, Select, TimePicker } from "antd";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { getTimeSlotById, updateTimeSlot } from "../../store/slices/timeSlotSlice";
import { useParams } from "react-router-dom";

const EditTimeSlot = () => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();
    const { id } = useParams()
    useEffect(() => {
        dispatch(getTimeSlotById(id))
    }, [dispatch, id])
    const { timeSlot } = useSelector((state) => state?.timeSlot)
    const [buttonLoading, setButtonLoading] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");

    useEffect(() => {
        if (timeSlot) {
            form.setFieldsValue({
                day: timeSlot?.data?.day,
                time_slot: timeSlot?.data?.time_slot?.map(slot => ({
                    start_time: dayjs(slot.start_time, "HH:mm"),
                    end_time: dayjs(slot.end_time, "HH:mm"),
                })),
            });
        }
    }, [timeSlot, form]);

    const handleFinish = async (values) => {
        setButtonLoading(true);

        const time_slot = values.time_slot.map(item => ({
            start_time: item.start_time.format("HH:mm"),
            end_time: item.end_time.format("HH:mm"),
        }));

        const data = {
            id: id,
            day: values.day,
            time_slot,
        };

        try {
            const resultAction = await dispatch(updateTimeSlot(data));

            if (updateTimeSlot.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction.payload?.message || "Time slot updated successfully");
                setAlertType("success");

                setTimeout(() => setAlertMessage(""), 4000);
            } else {
                setAlertMessage(resultAction.payload?.error || "Error updating time slot");
                setAlertType("error");
            }
        } catch (error) {
            setAlertMessage("Failed to update time slot");
            setAlertType("error");
        } finally {
            setButtonLoading(false);
        }
    };

    return (
        <Card title="Edit Time Slot" bordered={false}>
            {alertMessage && (
                <Alert
                    message={alertMessage}
                    type={alertType}
                    showIcon
                    icon={alertType === "success" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                    closable
                    onClose={() => setAlertMessage("")}
                    style={{ marginBottom: 24, borderRadius: 8 }}
                />
            )}

            <Form
                layout="vertical"
                form={form}
                onFinish={handleFinish}
            >
                <Form.Item
                    label="Day"
                    name="day"
                    rules={[{ required: true, message: "Please select day" }]}
                >
                    <Select
                        placeholder="Select day"
                        options={[
                            { value: "Mon", label: "Mon" },
                            { value: "Tue", label: "Tue" },
                            { value: "Wed", label: "Wed" },
                            { value: "Thu", label: "Thu" },
                            { value: "Fri", label: "Fri" },
                            { value: "Sat", label: "Sat" },
                            { value: "Sun", label: "Sun" },
                        ]}
                    />
                </Form.Item>

                <Form.List name="time_slot">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field, index) => (
                                <Card
                                    key={field.key}
                                    style={{ marginBottom: 20, background: "#fafafa" }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                                        <h4>Time Slot #{index + 1}</h4>
                                        {index > 0 && (
                                            <Button danger onClick={() => remove(field.name)}>
                                                Remove
                                            </Button>
                                        )}
                                    </div>

                                    <Form.Item
                                        label="Start Time"
                                        name={[field.name, "start_time"]}
                                        rules={[{ required: true, message: "Select start time" }]}
                                    >
                                        <TimePicker style={{ width: "100%" }} format="HH:mm" />
                                    </Form.Item>

                                    <Form.Item
                                        label="End Time"
                                        name={[field.name, "end_time"]}
                                        rules={[{ required: true, message: "Select end time" }]}
                                    >
                                        <TimePicker style={{ width: "100%" }} format="HH:mm" />
                                    </Form.Item>
                                </Card>
                            ))}

                            <Button type="dashed" onClick={() => add()} block>
                                + Add Another Time Slot
                            </Button>
                        </>
                    )}
                </Form.List>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={buttonLoading}
                        style={{ marginTop: 16 }}
                    >
                        Update
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
};

export default EditTimeSlot;